import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment/moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { rolePermissionApi } from 'api/rolePermission';

export const usersApi = createApi({
  tagTypes: ['USER', 'USER_LIST', 'USER_FILES', 'USER_STATUS', 'PASS_CHANGE', 'USER_NAME', 'CURRENT_USER'],
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    currentUser: builder.query<any, { userId: string }>({
      query: (args) => {
        const { userId } = args;
        return {
          url: `/uaa/api/current/${userId}`,
        };
      },
      transformResponse: (response: any) => {
        return { ...response?.data } || {};
      },
      providesTags: () => ['CURRENT_USER'],
    }),
    userCreate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/uaa/api',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(rolePermissionApi.util.invalidateTags(['ROLE_LIST']));
      },
      invalidatesTags: () => ['USER_LIST'],
    }),
    userUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/uaa/api',
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(rolePermissionApi.util.invalidateTags(['ROLE_LIST']));
      },
      invalidatesTags: () => ['USER_LIST', 'USER'],
    }),
    updateUserName: builder.mutation<any, any>({
      query: (data) => ({
        url: '/uaa/api/name',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['USER_NAME', 'CURRENT_USER'],
    }),
    userById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/uaa/api/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['USER', 'USER_FILES', 'USER_STATUS', 'PASS_CHANGE', 'USER_NAME'],
    }),
    users: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/uaa/api?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const content = response?.data?.content?.[0];
        const mapping: Record<string, { name: string; id: string }> = {};
        (content.roleResponses || []).forEach((i: { name: string; id: string }) => {
          mapping[i.id] = i;
        });
        response.data.content = (content?.adminResponses || {}).map((item: any) => ({
          ...item,
          roles: (item.roleIds || []).map((i: string) => mapping[i]),
          formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YY HH:mm:ss'),
          formattedCreateDate: moment(item.createDate).format('DD.MM.YY HH:mm:ss'),
          key: item.id,
          label: item.name,
          value: item.id,
        }));

        return response;
      },
      providesTags: () => ['USER_LIST'],
    }),
    addUserFile: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/admin',
        method: 'post',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(['CURRENT_USER']));
      },
      invalidatesTags: () => ['USER_FILES'],
    }),
    deleteUserFile: builder.mutation<any, any>({
      query: (data) => ({
        url: '/fss/api/admin',
        method: 'delete',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(usersApi.util.invalidateTags(['CURRENT_USER']));
      },
      invalidatesTags: () => ['USER_FILES'],
    }),
    resetUserPassword: builder.mutation<any, any>({
      query: (data: { email: string }) => ({
        url: '/uaa/api/password-reset',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['PASS_CHANGE'],
    }),
    changeUserPassword: builder.mutation<any, any>({
      query: (data: { id: string; currentPassword: string; password: string; passwordRepeat: string }) => ({
        url: '/uaa/api/change-password',
        method: 'post',
        body: data,
      }),
      invalidatesTags: () => ['PASS_CHANGE'],
    }),
    resendInvitation: builder.mutation<any, any>({
      query: (data) => ({
        url: `/uaa/api/resend-invitation`,
        method: 'post',
        body: data,
      }),
    }),
    activateUser: builder.mutation<any, any>({
      query: ({ id }: { id: string }) => ({
        url: `/uaa/api/reactivate/${id}`,
        method: 'put',
        body: {},
      }),
      invalidatesTags: () => ['USER_STATUS'],
    }),
    deactivateUser: builder.mutation<any, any>({
      query: (data) => ({
        url: `/uaa/api/deactivate`,
        method: 'put',
        body: data,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(rolePermissionApi.util.invalidateTags(['ROLE_LIST']));
      },
      invalidatesTags: () => ['USER_STATUS', 'USER_LIST'],
    }),
    departmentsList: builder.query<any, void>({
      query: () => ({
        url: '/uaa/api/departments',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
    }),
  }),
});

export const {
  useUserByIdQuery,
  useUsersQuery,
  useUserCreateMutation,
  useUserUpdateMutation,
  useAddUserFileMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDeleteUserFileMutation,
  useResetUserPasswordMutation,
  useChangeUserPasswordMutation,
  useDepartmentsListQuery,
  useCurrentUserQuery,
  useUpdateUserNameMutation,
  useResendInvitationMutation,
} = usersApi;
